import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Button, Divider, Dropdown, Modal } from "semantic-ui-react";

import type { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";
import { CheckboxHeader } from "simplydo/react/components/CheckboxHeader";
import { RadioCard } from "simplydo/react/components/RadioCard";

import { useAppSelector } from "store";
import util from "utils/utils";

import ConfigurableTable from "components/lib/ConfigurableTable";

type AssessmentReportModalProps = {
  isOpen: boolean;
  generateAssessmentReport: (
    assessmentRound:
      | NonNullable<OpenAPI.Schemas["IdeaTemplate"]["assessments"]>["rounds"]
      | null,
    includeNotes: boolean,
    selectedFields: string[],
  ) => void;
  onClose: () => void;
  challenge: OpenAPI.GET<"/challenges/{id}">["response"];
};

const templateFieldTypes = [
  { key: "shorttext", name: "Short text (one line)" },
  { key: "longtext", name: "Long text (paragraphs)" },
  { key: "numerictext", name: "Numerical text" },
  { key: "slider", name: "Slider" },
  { key: "dropdown", name: "Dropdown box" },
  { key: "optionMultiple", name: "Multiple selection" },
];

type FieldItem = {
  sectionName: string;
  field: { id: string; title?: string; type?: string };
};

const BusinessProfileFields: FieldItem[] = [
  {
    sectionName: "Organisation profile",
    field: { id: "name", title: "Organisation Name" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "phoneNumber", title: "Phone number" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "websiteUrl", title: "Website URL" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "description", title: "Description" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "address.streetAddress", title: "Street address" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "address.country", title: "Country" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "address.region", title: "Region" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "address.city", title: "City" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "address.postcode", title: "Postcode" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "size", title: "Organisation size" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "type", title: "Organisation type" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "foundingYear", title: "Founding year" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "status", title: "Organisation status" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "creditsafeRegNo", title: "Organisation registration number" },
  },
  {
    sectionName: "Organisation profile",
    field: { id: "vatNo", title: "VAT registration number" },
  },
];

export const AssessmentReportModal = ({
  isOpen,
  generateAssessmentReport,
  onClose,
  challenge,
}: AssessmentReportModalProps) => {
  const [includeNotes, setIncludeNotes] = useState<boolean>(false);
  const [exportWithIdeaFields, setExportWithIdeaFields] =
    useState<boolean>(false);
  const [selectedFields, setSelectedFields] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFieldType, setSearchFieldType] = useState("");
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);

  const template = challenge?.ownerIdeaTemplate;
  const assessmentRounds = template?.assessments?.rounds || [];

  const [selectedAssessmentRound, setSelectedAssessmentRound] = useState(
    assessmentRounds.length > 0 ? assessmentRounds[0] : null,
  );

  const toggleFieldSelection = (fieldId) => {
    setSelectedFields((prev) =>
      prev.includes(fieldId) ?
        prev.filter((id) => id !== fieldId)
      : [...prev, fieldId],
    );
  };

  const businessProfileSection = challenge?.ownerIdeaTemplate?.body?.find(
    (section) => section.type === "businessProfile",
  );

  const canViewEdi = util.hasPermission(
    user,
    "org.viewEdi",
    challenge?.organisation,
  );
  const flattenedFields = useMemo(
    () =>
      (
        template?.body
          ?.filter(
            (section) =>
              section.type === "custom" ||
              (section.type === "edi" && canViewEdi) ||
              !section.type,
          )
          .flatMap((section) =>
            (section.fields || []).map((field) => {
              return {
                sectionName: section.name || "Untitled section",
                field: field,
              };
            }),
          ) as FieldItem[]
      ).concat(businessProfileSection ? BusinessProfileFields : []) || [],
    [businessProfileSection, template?.body],
  );

  const filteredFields = useMemo(() => {
    return flattenedFields.filter((item) => {
      const matchesSearch = item.sectionName
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesType =
        searchFieldType ? item.field.type === searchFieldType : true;

      return matchesSearch && matchesType;
    });
  }, [flattenedFields, searchTerm, searchFieldType]);

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={isOpen}
    >
      <Modal.Header>Generate assessment report</Modal.Header>
      <Modal.Content scrolling>
        <p>
          Download an Excel report of all assessments from this{" "}
          {t("generic.challenge")}.
        </p>
        <RadioCard.Group
          onValueChange={(value) =>
            setSelectedAssessmentRound(
              assessmentRounds.find((round) => round.id === value)!,
            )
          }
          defaultValue={selectedAssessmentRound?.id || "legacy"}
          editable
        >
          {assessmentRounds.map((round) => (
            <RadioCard.Item key={round.id} value={round.id}>
              {round.name}
            </RadioCard.Item>
          ))}
        </RadioCard.Group>
        <Divider section />
        <CheckboxHeader
          checked={includeNotes}
          onChange={() => setIncludeNotes(!includeNotes)}
          header="Include assessor notes"
          as="h5"
          description="Include any notes the assessor left for each of their assessments."
        />
        <CheckboxHeader
          checked={exportWithIdeaFields}
          onChange={() => setExportWithIdeaFields(!exportWithIdeaFields)}
          as="h5"
          header={`Include ${t("generic.idea")} fields`}
          description={`Include fields from the ${t("generic.idea")} in the report.`}
        />
        {exportWithIdeaFields && (
          <>
            <h5>Selected fields</h5>
            <p>Select the fields you want to include in the report.</p>
            <div style={{ display: "flex", gap: "1px", marginBottom: "10px" }}>
              <div style={{ marginBottom: "5px", flex: 1, minWidth: "200px" }}>
                <Input
                  icon="search"
                  placeholder="Search by section name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div style={{ marginBottom: "5px", minWidth: "200px" }}>
                <Dropdown
                  placeholder="Filter by field type"
                  clearable
                  selection
                  options={templateFieldTypes.map((type) => ({
                    key: type.key,
                    value: type.key,
                    text: type.name,
                  }))}
                  value={searchFieldType}
                  onChange={(_e, { value }) =>
                    setSearchFieldType(value as string)
                  }
                />
              </div>
            </div>
            <ConfigurableTable
              containerStyle={{ maxHeight: 400, overflowY: "auto" }}
              tableKey="challengeSectionFieldImport"
              selectedKeys={selectedFields}
              keyExtractor={(item) => item.field.id}
              onSelect={(item) => {
                toggleFieldSelection(item.field.id);
              }}
              preventSelectAll
              columns={[
                { key: "sectionName", name: "Section" },
                {
                  key: "fieldTitle",
                  name: "Field",
                  render: ({ item }) => (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <p style={{ margin: 0 }}>
                        {item.field.type === "row" ?
                          "Row divider"
                        : item.field.type === "infotext" ?
                          "Information text"
                        : item.field.title}
                      </p>
                    </div>
                  ),
                },
              ]}
              data={filteredFields}
            />
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          primary
          onClick={() =>
            generateAssessmentReport(
              selectedAssessmentRound,
              includeNotes,
              selectedFields,
            )
          }
        >
          Generate Report
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
